import { centeredMainGridClasses } from '@/styles/grid';
import clsx from 'clsx';
import Dropdown, { Option } from 'components/src/dropdown';
import * as React from 'react';
import Section from '@/components/Section';
import { useIsMedium } from '@/utils/hooks';
import { CountryData, RoamingTab } from './types';
import CountryRoamingData from './SelectedCountry/CountryRoamingData';
import RoamingCards from './RoamingCards';
import { TabGroupFieldsFragment, LargeCardFieldsFragment } from 'types/generated';
import { Maybe } from 'graphql/jsutils/Maybe';
import dynamic from 'next/dynamic';
import GlobePlaceholder from './Globe/GlobePlaceholder';
import { AnimatePresence } from 'framer-motion';
import InfoSetSection from '../InfoSetSection';
const Globe = dynamic(() => import('./Globe/Globe'), {
  loading: () => (
    <div className="h-[400px]">
      <GlobePlaceholder />
    </div>
  ),
  ssr: false,
});

type Props = {
  infoSetId: string | '';
  firstTabCollection: Maybe<TabGroupFieldsFragment>;
  reikiCountries: Array<CountryData>;
};

const RoamingInfo = ({ infoSetId, reikiCountries, firstTabCollection }: Props) => {
  const isMedium = useIsMedium();
  const [activeTab, setActiveTab] = React.useState(RoamingTab.TravellingAbroad);
  const [selectedCountryOption, setSelectedCountryOption] = React.useState<Option | null>(null);
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    setHydrated(true);
  }, []);

  const selectedCountryData = React.useMemo(() => {
    if (selectedCountryOption === null) {
      return null;
    }
    return reikiCountries.find((row) => row.countryCode === selectedCountryOption.value);
  }, [reikiCountries, selectedCountryOption]);
  const tabs = firstTabCollection?.tabsCollection?.items;

  const extraProductCards = React.useMemo(() => {
    return (tabs ?? []).reduce<Record<string, LargeCardFieldsFragment[]>>((acc, curr) => {
      if (curr?.content?.__typename === 'CardCollection') {
        const items = curr.content.cardCollection?.items;
        if (items) {
          const key = curr.id?.toLocaleLowerCase() || '';
          acc[key] = (acc[key] || []).concat(items as LargeCardFieldsFragment[]);
        }
      }
      return acc;
    }, {});
  }, [tabs]);

  if (!hydrated) {
    return <div />;
  }

  return (
    <Section
      childrenTypeName={undefined}
      sectionId={undefined}
      marginTop={32}
      marginBottom={32}
      gridType="full"
      theme={isMedium ? 'white-15' : 'white-100'}
    >
      <div
        className={clsx(
          centeredMainGridClasses,
          'overflow-hidden bg-white-100 sm:py-64 md:rounded-28 md:border-1 md:border-black-25'
        )}
      >
        <div className={'col-span-full flex flex-col items-center md:col-span-6 md:col-start-4'}>
          <InfoSetSection infoSetId={infoSetId} />
          <div className="mb-32 w-full max-w-[336px]">
            <Dropdown
              id="sup"
              placeholder="Veldu land..."
              options={reikiCountries.map((row) => ({ label: row.countryNameIS, value: row.countryCode }))}
              selectedOption={selectedCountryOption}
              onChange={setSelectedCountryOption}
              isSearchable={true}
            />
          </div>
          {selectedCountryOption === null && (
            <div className="relative">
              <Globe showMarker={false} />
            </div>
          )}
        </div>
        {selectedCountryData && (
          <CountryRoamingData countryData={selectedCountryData} activeTab={activeTab} setActiveTab={setActiveTab} />
        )}
        <AnimatePresence mode="popLayout">
          {selectedCountryData && (selectedCountryData.travelPackage || activeTab !== RoamingTab.TravellingAbroad) && (
            <RoamingCards
              cards={extraProductCards}
              activeTab={selectedCountryData ? activeTab : null}
              selectedCountryData={selectedCountryData}
            />
          )}
        </AnimatePresence>
      </div>
    </Section>
  );
};

export default RoamingInfo;
